'use client';

import type { FC } from 'react';
import type {
  SwitchGlobalFilterItem,
  ChipGlobalFilterItem,
  CheckBoxListGlobalFilterItem,
  AccordionGlobalFilterItem,
  HistogramNumberRangeGlobalFilterItem,
  HistogramNumberRangeWithoutUpperLimitGlobalFilterItem,
  GlobalFilterItem,
} from '@weberus/shared-types';
import { produce } from 'immer';
import {
  AccordionTypeFilter,
  CheckboxTypeFilter,
  ChipTypeFilter,
  HistogramTypeFilter,
  SwitchTypeFilter,
} from './filter';

const isSwitchTypeDetailFilter = (item: GlobalFilterItem): item is SwitchGlobalFilterItem => {
  return item.type === 'SWITCH';
};
const isChipTypeDetailFilter = (item: GlobalFilterItem): item is ChipGlobalFilterItem => {
  return item.type === 'CHIPS';
};
const isCheckboxListTypeDetailFilter = (item: GlobalFilterItem): item is CheckBoxListGlobalFilterItem => {
  return item.type === 'CHECKBOX_LIST';
};
const isAccordionTypeDetailFilter = (item: GlobalFilterItem): item is AccordionGlobalFilterItem => {
  return item.type === 'ACCORDION';
};
const isHistogramNumberRangeTypeDetailFilter = (
  item: GlobalFilterItem,
): item is HistogramNumberRangeGlobalFilterItem => {
  return item.type === 'HISTOGRAM_NUMBER_RANGE';
};
const isHistogramNumberRangeWithoutUpperLimitTypeDetailFilter = (
  item: GlobalFilterItem,
): item is HistogramNumberRangeWithoutUpperLimitGlobalFilterItem => {
  return item.type === 'FLAT_NUMBER_RANGE_WITHOUT_UPPER_LIMIT';
};

interface FilterRendererProps {
  filter: GlobalFilterItem;
  index: number;
  description?: string;
  setFilterState: (index: number, newState: GlobalFilterItem) => void;
  onClickHistogramHandle: (serverLogMeta: GlobalFilterItem['serverLogMeta'], minMax: 'min' | 'max') => void;
  onFocusHistogramInput: (serverLogMeta: GlobalFilterItem['serverLogMeta'], minMax: 'min' | 'max') => void;
  onMoreButtonClick: (serverLogMeta: GlobalFilterItem['serverLogMeta']) => void;
}

export const FilterRenderer: FC<FilterRendererProps> = ({
  filter,
  index,
  description,
  setFilterState,
  onClickHistogramHandle,
  onFocusHistogramInput,
  onMoreButtonClick,
}) => {
  const handleItemClick = (idx: number): void => {
    if (!isSwitchTypeDetailFilter(filter) && !isChipTypeDetailFilter(filter) && !isCheckboxListTypeDetailFilter(filter))
      return;
    const newState = produce(filter, (draft) => {
      draft.items[idx].selected = !draft.items[idx].selected;
    });
    setFilterState(index, newState);
  };

  const handleChipClick = ({
    accordionIdx,
    chipIdx,
    isSelectAllItem,
  }: {
    accordionIdx: number;
    chipIdx: number;
    isSelectAllItem: boolean;
  }): void => {
    if (!isAccordionTypeDetailFilter(filter)) return;

    const newState = produce(filter, (draft) => {
      if (isSelectAllItem) {
        // 전체 선택 항목 선택 시, 해당 아코디언의 다른 아이템들은 선택 해제
        draft.items[accordionIdx].items.forEach((item) => {
          item.selected = item.isSelectAllItem ? !item.selected : false;
        });
      } else {
        // 전체 선택 항목이 아닌 경우, 전체 선택 항목 선택 해제
        const selectAllItemIdx = draft.items[accordionIdx].items.findIndex((item) => item.isSelectAllItem);
        if (selectAllItemIdx !== -1) {
          draft.items[accordionIdx].items[selectAllItemIdx].selected = false;
        }
        draft.items[accordionIdx].items[chipIdx].selected = !draft.items[accordionIdx].items[chipIdx].selected;
      }
    });
    setFilterState(index, newState);
  };
  const handleRangeChange = (min: number, max: number): void => {
    if (!isHistogramNumberRangeTypeDetailFilter(filter)) return;

    const newState = produce(filter, (draft) => {
      draft.items[0].selected = filter.range.min !== min || filter.range.max !== max;
      draft.items[0].value = { from: min, to: max };
    });
    setFilterState(index, newState);
  };
  const handleRangeChangeWithoutUpperLimit = (min: number, max: number): void => {
    if (!isHistogramNumberRangeWithoutUpperLimitTypeDetailFilter(filter)) return;

    const newState = produce(filter, (draft) => {
      draft.items[0].selected = filter.range.min !== min || filter.range.max !== max;
      draft.items[0].value = { from: min, to: max };
    });
    setFilterState(index, newState);
  };

  // 스위치 타입 필터
  if (isSwitchTypeDetailFilter(filter)) {
    return (
      <SwitchTypeFilter
        title={filter.title}
        selected={filter.items?.[0].selected}
        onClick={() => {
          handleItemClick(0);
        }}
      />
    );
  }
  // 칩 타입 필터
  if (isChipTypeDetailFilter(filter)) {
    return (
      <ChipTypeFilter
        {...filter}
        onClick={(idx: number) => {
          handleItemClick(idx);
        }}
      />
    );
  }
  // 체크박스 리스트 타입 필터
  if (isCheckboxListTypeDetailFilter(filter)) {
    return (
      <CheckboxTypeFilter
        {...filter}
        onMoreButtonClick={onMoreButtonClick}
        onClick={(idx: number) => {
          handleItemClick(idx);
        }}
      />
    );
  }
  // 아코디언 타입 필터
  if (isAccordionTypeDetailFilter(filter)) {
    return <AccordionTypeFilter {...filter} onChipClick={handleChipClick} onMoreButtonClick={onMoreButtonClick} />;
  }
  // 히스토그램 넘버 레인지 타입 필터
  if (isHistogramNumberRangeTypeDetailFilter(filter)) {
    return (
      <HistogramTypeFilter
        {...filter}
        description={description}
        onRangeChange={handleRangeChange}
        onClickHistogramHandle={onClickHistogramHandle}
        onFocusHistogramInput={onFocusHistogramInput}
      />
    );
  }

  if (isHistogramNumberRangeWithoutUpperLimitTypeDetailFilter(filter)) {
    return (
      <HistogramTypeFilter
        {...filter}
        description={description}
        onRangeChange={handleRangeChangeWithoutUpperLimit}
        onClickHistogramHandle={onClickHistogramHandle}
        onFocusHistogramInput={onFocusHistogramInput}
      />
    );
  }
  return null;
};

export const globalFilterTypeCheckers = {
  isSwitchType: isSwitchTypeDetailFilter,
  isChipType: isChipTypeDetailFilter,
  isCheckboxListType: isCheckboxListTypeDetailFilter,
  isAccordionType: isAccordionTypeDetailFilter,
  isHistogramNumberRangeType: isHistogramNumberRangeTypeDetailFilter,
  isHistogramNumberRangeWithoutUpperLimitType: isHistogramNumberRangeWithoutUpperLimitTypeDetailFilter,
};
